import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { backendApi } from '@/lib/backendApi';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

const PlatformBillingPage = () => {
  const { api } = useAuth();
  const [adminKey, setAdminKey] = useState('');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = adminKey ? { 'x-platform-admin-key': adminKey } : {};

  const loadPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await backendApi.platform.listBillingPayments(api, null, headers);
      setPayments(response.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load payment queue.');
    } finally {
      setLoading(false);
    }
  };

  const review = async (paymentId, action) => {
    setLoading(true);
    setError('');
    try {
      await backendApi.platform.reviewBillingPayment(api, paymentId, { action }, headers);
      await loadPayments();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to review payment.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-5xl mx-auto">
        <Card className="border-border/60 bg-card/75 shadow-xl backdrop-blur">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Platform Billing Queue</CardTitle>
            <CardDescription>
              Platform admins can review uploaded payment proofs here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-[1fr_auto] gap-3">
              <PasswordInput
                className="h-11"
                placeholder="Platform admin key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
              />
              <Button
                type="button"
                onClick={loadPayments}
                disabled={loading || !adminKey}
                className="h-11"
              >
                {loading ? 'Loading...' : 'Load Queue'}
              </Button>
            </div>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-3">
              {payments.map((payment) => (
                <div key={payment.id} className="rounded-xl border border-border/60 bg-background/70 p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold">Payment #{payment.id?.slice?.(-8) || payment.id}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>Amount: {payment.amount}</span>
                        <Badge variant="outline" className="capitalize">{payment.status}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        disabled={loading}
                        onClick={() => review(payment.id, 'approve')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Approve
                      </Button>
                      <Button
                        type="button"
                        disabled={loading}
                        variant="destructive"
                        onClick={() => review(payment.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {!payments.length ? <div className="text-sm text-muted-foreground">No payments loaded.</div> : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlatformBillingPage;
